import type { VentaPendienteMaster } from '../services/ventasSeguimientoService';
import type { OrdenReparacionItemDto, OrdenReparacionReadDto } from '../services/ordenReparacionService';
import type { POSLinea } from '../pages/facturaventa/pos/types';
import { masterFieldsParaLinea } from '../pages/facturaventa/pos/lineaMasterCliente';

/** Tipo formulario venta para orden de trabajo (debe coincidir con el backend). */
export const NRO_TIPO_FORMULARIO_ORDEN_TRABAJO = '18';

const genId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const nroFormularioOrdenTrabajo = (orden: OrdenReparacionReadDto): number => {
  const nfm = Number(orden.nroFormulario ?? orden.id ?? 0);
  return Number.isFinite(nfm) && nfm > 0 ? nfm : 0;
};

/** Identificador canónico para rutas y GET/PUT/DELETE (prioriza nroFormulario). */
export const idOrdenTrabajoApi = (orden: OrdenReparacionReadDto): string => {
  const nfm = nroFormularioOrdenTrabajo(orden);
  if (nfm > 0) return String(nfm);
  if (orden.id != null && orden.id !== '') return String(orden.id);
  return '';
};

export const ordenTrabajoEstaFacturada = (orden: OrdenReparacionReadDto): boolean => {
  const estado = String(orden.estado ?? '').trim().toLowerCase();
  return orden.facturada === true || estado === 'facturada' || estado === 'facturado';
};

export const ordenTrabajoEsEditable = (orden: OrdenReparacionReadDto): boolean =>
  !ordenTrabajoEstaFacturada(orden);

export const labelEstadoOrdenTrabajo = (orden: OrdenReparacionReadDto): string => {
  if (ordenTrabajoEstaFacturada(orden)) return 'Facturada';
  const estado = String(orden.estado ?? '').trim();
  return estado || 'Pendiente';
};

export const puedeFacturarOrdenTrabajo = (orden: OrdenReparacionReadDto): boolean => {
  const estado = String(orden.estado ?? '').trim().toLowerCase();
  if (orden.facturada === true || estado === 'facturada' || estado === 'facturado') {
    return false;
  }
  const items = orden.items ?? [];
  if (items.length === 0) return false;
  return items.some((it) => pendienteFacturarItem(it) > 0);
};

export const pendienteFacturarItem = (item: OrdenReparacionItemDto): number => {
  const pf = Number(item.pendienteFacturar);
  if (Number.isFinite(pf) && pf >= 0) return pf;
  return Number(item.cantidad ?? 0) || 0;
};

export const ordenTrabajoItemToPendienteMaster = (
  orden: OrdenReparacionReadDto,
  item: OrdenReparacionItemDto,
  index: number,
): VentaPendienteMaster => {
  const nroFormularioMaster = nroFormularioOrdenTrabajo(orden);
  const nroItemMaster = Number(item.nroItemMaster ?? index + 1);
  const pf = pendienteFacturarItem(item);
  const precioLista = Number(item.precioLista ?? item.precioUnitario ?? 0) || 0;

  return {
    nroFormularioMaster,
    nroItemMaster,
    nroCliente: orden.nroCliente,
    nombreCliente: orden.nombreCliente ?? undefined,
    codigoArticulo: item.codigoArticulo,
    descripcionArticulo: item.descripcion,
    nroTipoFormularioMaster: NRO_TIPO_FORMULARIO_ORDEN_TRABAJO,
    letraComprobanteMaster: orden.serie || 'A',
    nroSucComprobanteMaster: undefined,
    nroComprobanteMaster: undefined,
    fechaEmisionComprobanteMaster: orden.fechaEmision || null,
    cantidadPedida: Number(item.cantidad ?? 0) || 0,
    cantidadFacturada: Math.max(0, (Number(item.cantidad ?? 0) || 0) - pf),
    cantidadRemitida: 0,
    cantidadCancelada: 0,
    cantidadDevuelta: 0,
    pendienteFacturar: pf,
    pendienteEntregar: 0,
    precioListaMaster: precioLista,
    precioUnitarioMaster: Number(item.precioUnitario ?? precioLista) || precioLista,
    porcDesc1Master: Number(item.porcDesc ?? 0) || 0,
    notas: orden.detalleReparacion || undefined,
    estadoMaster: orden.estado ?? undefined,
  };
};

export const ordenTrabajoToPendientesMaster = (orden: OrdenReparacionReadDto): VentaPendienteMaster[] =>
  (orden.items ?? [])
    .map((item, idx) => ordenTrabajoItemToPendienteMaster(orden, item, idx))
    .filter((p) => Number(p.pendienteFacturar) > 0);

export const lineaPosDesdeItemOrdenTrabajo = (
  orden: OrdenReparacionReadDto,
  item: OrdenReparacionItemDto,
  index: number,
  nroCliente: number,
): POSLinea => {
  const pf = pendienteFacturarItem(item);
  const cantidad = pf > 0 ? pf : Number(item.cantidad ?? 0) || 0;
  const precioLista = Number(item.precioLista ?? item.precioUnitario ?? 0) || 0;
  const porcDesc = Math.max(0, Math.min(100, Number(item.porcDesc ?? 0) || 0));
  const nroFormularioMaster = nroFormularioOrdenTrabajo(orden);
  const nroItemMaster = Number(item.nroItemMaster ?? index + 1);

  const linea: POSLinea = {
    id: genId(),
    codigoArticulo: String(item.codigoArticulo ?? '').trim(),
    descripcionArticulo: String(item.descripcion ?? '').trim(),
    cantidad,
    precioUnitario: precioLista,
    descuento: porcDesc,
    subtotal: 0,
    um: String(item.unidadMedida ?? 'UNI').trim(),
    ...(nroFormularioMaster > 0 && nroItemMaster > 0
      ? masterFieldsParaLinea(nroFormularioMaster, nroItemMaster, nroCliente)
      : {}),
  };
  linea.subtotal = Math.round(cantidad * precioLista * (1 - porcDesc / 100) * 100) / 100;
  return linea;
};
